import { FieldErrors } from "react-hook-form";
import { MaybeNull, ParsedQueryParams, UtilityBillFormData } from "types";
import { REQUIRED_VALIDATION_MESSAGE, WARNING_MESSAGES, EMPTY_CELL_VALUE } from "consts";
import { ConfigParameterDTO } from "api";
import { TextFieldColorState } from "components";
// IMPORTANT: Import page consts directly from their source files, NOT from the "pages" barrel.
// Importing from "pages" loads all page modules (including page.tsx files), which triggers
// a circular dependency chain: pages → initiatives/page.tsx → styles.ts → COLORS from "components"
// → partially initialized module → COLORS undefined. Direct imports avoid this entirely.
import { SCORECARD_PAGE } from "pages/authenticated/scorecard/consts";
import { TARGETS_PAGE } from "pages/authenticated/accelerate/targets/consts";
import { INITIATIVES_PAGE } from "pages/authenticated/accelerate/initiatives-management/consts";

export const isNullOrUndefined = (value: unknown) =>
  value === undefined || value === null;

/**
 * Safely parses a string to a number, returning null if the result is NaN or invalid.
 * Handles both European (comma as decimal) and US (comma as thousands separator) formats.
 * @param value - The string value to parse
 * @param options - Parsing options
 * @param options.decimalSeparator - If true, treats comma as decimal separator (European format)
 * @param options.defaultValue - Value to return if parsing fails (defaults to null)
 * @returns The parsed number or the default value if parsing fails
 */
export const safeParseNumber = (
  value: string | undefined | null,
  options: { decimalSeparator?: boolean; defaultValue?: number | null } = {},
): number | null => {
  const { decimalSeparator = false, defaultValue = null } = options;

  if (value === undefined || value === null) {
    return defaultValue;
  }

  // Trim whitespace and check for empty string
  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return defaultValue;
  }

  const normalizedValue = decimalSeparator
    ? trimmedValue.replace(/,/g, ".")
    : trimmedValue.replace(/,/g, "");

  const parsed = Number(normalizedValue);

  if (Number.isNaN(parsed)) {
    return defaultValue;
  }

  return parsed;
};

/**
 * Extracts a user-friendly error message from various error formats.
 * Handles API error responses, Error objects, and unknown error types.
 * Generic server errors (500 / "Internal server error") always resolve to
 * the fallback so users never see raw BE noise.
 *
 * @param error - The error object to extract the message from
 * @param fallbackMessage - Default message if no error message can be extracted,
 *                          or if the extracted message is a generic server error
 * @returns A string error message suitable for display to users
 */
const isServerError = (msg: string) =>
  msg.trim().toLowerCase() === "internal server error";

export const extractErrorMessage = (
  error: unknown,
  fallbackMessage: string = "An unexpected error occurred",
): string => {
  if (!error) {
    return fallbackMessage;
  }

  // Handle string errors directly
  if (typeof error === "string") {
    return isServerError(error) ? fallbackMessage : error;
  }

  // Handle error-like objects
  if (typeof error === "object") {
    const err = error as Record<string, unknown>;

    // Check for API response error format (axios-style)
    const responseData = (err.response as Record<string, unknown>)?.data;
    if (responseData) {
      // If response.data is a string, use it directly
      if (typeof responseData === "string") {
        return isServerError(responseData) ? fallbackMessage : responseData;
      }
      // If response.data is an object, check for common error properties
      if (typeof responseData === "object") {
        const data = responseData as Record<string, unknown>;
        if (typeof data.error === "string")
          return isServerError(data.error) ? fallbackMessage : data.error;
        if (typeof data.message === "string")
          return isServerError(data.message) ? fallbackMessage : data.message;
      }
    }

    // Check for standard Error object properties
    if (typeof err.message === "string") {
      return isServerError(err.message) ? fallbackMessage : err.message;
    }
  }

  return fallbackMessage;
};

export const checkNullValue = (value: any) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return value;
}

export const definedValueOrDefault = <TValue, TDefault, TReturn = TDefault>(
  value: MaybeNull<TValue>,
  defaultValue: TDefault,
  mutationFn?: (value: TValue) => TReturn,
): TReturn | TDefault => {
  if (isNullOrUndefined(value)) {
    return defaultValue;
  }

  if (!mutationFn) {
    return value as unknown as TReturn;
  }

  return mutationFn(value!);
};

export const toUSNumber = (v: number | string, isCurrency = false) => {
  const navLanguage =
    window.sessionStorage.getItem("navigator_language") &&
    JSON.parse(window.sessionStorage.getItem("navigator_language") || "");

  return Number(v).toLocaleString(
    `${navLanguage || "en-GB"}`,
    isCurrency
      ? {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
      : undefined,
  );
};

export const getLoggedUserData = () => {
  const loggedUserData =
    window.sessionStorage.getItem("user") &&
    JSON.parse(window.sessionStorage.getItem("user") || "");

  return loggedUserData;
};

export const buildQueryParams = (
  query: Record<string, string | number | boolean | string[] | number[]>,
) => {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length !== 0) {
        params.append(key, value.join());
      }
    } else if (!isNullOrUndefined(value) && value !== "" && value !== false) {
      params.append(key, String(value));
    }
  });

  return params;
};

export const getParsedQueryParams = (searchParams: URLSearchParams) => {
  const queryParams: ParsedQueryParams = {};

  searchParams.forEach((value, key) => {
    if (!queryParams[key]) {
      queryParams[key] = value;
    } else {
      if (!Array.isArray(queryParams[key])) {
        queryParams[key] = [queryParams[key]] as string[];
      }

      (queryParams[key] as string[]).push(value);
    }
  });

  return queryParams;
};

export const sortBy = <T>({
  array,
  key,
  order = "ASC",
}: {
  array: T[];
  key?: keyof T;
  order?: "ASC" | "DESC";
}) => {
  if (key) {
    if (order !== "ASC") {
      return array.sort((a, b) =>
        a[key] > b[key] ? -1 : b[key] > a[key] ? 1 : 0,
      );
    }

    return array.sort((a, b) =>
      a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0,
    );
  }

  if (order !== "ASC") {
    return array.sort((a, b) => (a > b ? -1 : b > a ? 1 : 0));
  }

  return array.sort((a, b) => (a > b ? 1 : b > a ? -1 : 0));
};

export const formatNumber = (num: number, decimalPlaces: number) => {
  const fixedNum = num?.toFixed(decimalPlaces);
  return parseFloat(fixedNum);
}

export const formatToUSNumber = (value: number) => {
  if (Number.isNaN(value) || isNullOrUndefined(value)) return EMPTY_CELL_VALUE;
  return new Intl.NumberFormat('en-US').format(value);
}

// Formats a number as a whole-dollar USD currency string using en-US locale.
// Decimal places are omitted entirely (maximumFractionDigits: 0), so 1234.9 → "$1,235".
// Example: 1000000 → "$1,000,000"
export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

// Formats a number as a locale string (en-US) with up to `decimals` decimal places (default: 1).
// Trailing zeros are not added (minimumFractionDigits: 0), so 5.0 → "5" and 5.5 → "5.5".
// Example: 1234567.8 → "1,234,567.8"
export const formatLocaleNumber = (value: number, decimals = 1): string =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(value);

export const formatNullable = (value: number | null | undefined, formatter: (v: number) => string): string =>
  value !== null && value !== undefined ? formatter(value) : EMPTY_CELL_VALUE;

export const convertBlankStringsToNull = (obj: any) => {
  const newObj: any = {};

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === "string" && obj[key].trim() === "") {
      newObj[key] = null;
    } else if (Array.isArray(obj[key])) {
      newObj[key] = obj[key].map((item: any) => convertBlankStringsToNull(item));
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      newObj[key] = convertBlankStringsToNull(obj[key]);
    } else {
      newObj[key] = obj[key];
    }
  });

  return newObj;
};

/**
 * Capitalizes the first letter and lowercases the rest of the string.
 *
 * @param str - The input string to format.
 * @returns A new string with only the first letter in uppercase and the rest in lowercase.
 */
export const capitalizeString = (str: string): string => {
  if (!str || typeof str !== "string") return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Capitalizes the first letter of each word in a string, using a separator to split the input.
 * Each word is transformed to have an uppercase first letter and lowercase remaining letters.
 *
 * @param str - The input string to format.
 * @param sep - The separator used to split the input string into words (e.g., "-" or "_").
 * @returns A new string with each word capitalized and separated by a space.
 */
export const capitalizeAllStringWithSeparator = (str: string, sep: string): string => {
  if (!str) return "";
  return str.split(sep).map(word => capitalizeString(word)).join(" ");
}

/**
 * Splits a string into an array using the provided separator and trims each resulting item.
 *
 * @param input - The input string to be split.
 * @param separator - The character or substring used to separate the input string.
 * @returns An array of trimmed strings. Returns an empty array if the input is empty or undefined.
 *
 */
export const parseStringBySeparator = (input: string, separator: string): string[] => {
  if (!input) return [];
  return input
    .split(separator)
    .map((item) => item.trim())
};

/* Combines selected dropdown option arrays based on a corresponding list of boolean flags.
*
* @param {boolean[]} optionals - Array of boolean values indicating which option arrays to include.
* Each `true` value at index `i` means `options[i]` will be included in the result.
* @param {Array<any>[]} options - Array of arrays containing dropdown options.
* Each inner array corresponds to a boolean in `optionals`.
*
* @returns {Array<any>} A flattened array containing only the option arrays marked as active (`true` in `optionals`).
* Returns an empty array if the lengths of `optionals` and `options` do not match.
*/
export const combineDropdownOptions = (optionals: boolean[], options: Array<any>): Array<any> => {
  if (optionals.length !== options.length) return [];

  return optionals.reduce((acc, isActive, index) => {
    if (isActive) {
      acc.push(...options[index]);
    }
    return acc;
  }, [] as Array<any>);
};

/**
 * Formats a status label by appending the count of missing fields for warning statuses.
 * 
 * @param statusKey - The status key/label to format
 * @param missingFields - Array of missing field names
 * @returns The formatted status label. If statusKey is "Warning (Missing Fields)" 
 *          and there are missing fields, appends the count in parentheses (e.g., 
 *          "Warning (Missing Fields) (3)"). Otherwise returns the original statusKey.
 */
export const getStatusLabel = (statusKey: string, missingFields: string[]) => {
  if (statusKey === "Warning (Missing Fields)") {
    return `Warning (Missing Fields) ${missingFields.length ? `(${missingFields.length})` : ""}`;
  }
  return statusKey;
};

/**
 * Gets the corresponding color for a priority level indicator dot.
 * 
 * @param status - The status level (0-10)
 * @returns The hex color code string for the status level, or "none" if status is not mapped
 *
 */
export const getDotColor = (status: number) => {
  const colorMap: Record<number, string> = {
    0: "#DD1515",
    1: "#DD1515",
    2: "#DD1515",
    3: "#9EE766",
    4: "#9EE766",
    5: "#0E25D5",
    6: "#0E25D5",
    7: "#00A1FF",
    8: "#9EE766",
    9: "#7F7F7F",
    10: "#7F7F7F",
  };
  return colorMap[status] ?? "none";
};

/**
 * Determines the algorithm name based on service, source, and routing configuration.
 * 
 * @param row - Object containing service, source, and routing properties
 * @param row.service - The service type (e.g., "default")
 * @param row.source - The data source (e.g., "idpv1", "abbyy")
 * @param row.routing - The routing configuration (e.g., "fallback", "default")
 * @returns The algorithm name string based on the configuration, or "-" if no match found
 */
export const determineAlgorithmName = (row: any): string => {
  const { service, source, routing } = row;

  if (service === "default" && source === "idpv1" && routing === "fallback") {
    return "IDP Fallback";
  }

  if (service === "default" && source === "idpv1" && routing === "default") {
    return "IDP A/B Testing";
  }

  if (service === "default" && source === "abbyy") {
    return "Current";
  }

  return "-";
};

/**
 * Determines whether a given feature is enabled based on configuration parameters.
 *
 * Searches for a configuration parameter matching the provided feature name and evaluates
 * its `configValue`. Supports boolean values (`true`/`false`) and string representations
 * such as `"true"` or `"on"`.
 *
 * @param {ConfigParameterDTO[] | undefined} configParameter - The list of configuration parameters to search in.
 * @param {string} featureName - The name of the feature to check.
 * @returns {boolean} `true` if the feature is enabled, otherwise `false`.
 */
export const isFeatureEnabled = (configParameter: ConfigParameterDTO[] | undefined, featureName: string): boolean => {
  const featureValue = configParameter?.find((setting: ConfigParameterDTO) => setting.name === featureName)?.configValue;
  return typeof featureValue === "boolean" ? featureValue : featureValue === "true" || featureValue?.toLocaleLowerCase() === "on";
};

/**
 * Retrieves a configuration parameter object from a list by matching its `name` property with the provided feature name.
 *
 * @param {ConfigParameterDTO[] | undefined} configParameter - The list of configuration parameters to search in.
 * @param {string} featureName - The name of the feature whose configuration parameter should be retrieved.
 * @returns {ConfigParameterDTO | undefined} The matching configuration parameter if found, otherwise `undefined`.
 */
export const retrieveConfigParameter = (configParameter: ConfigParameterDTO[] | undefined, featureName: string): ConfigParameterDTO | undefined => configParameter?.find((setting: ConfigParameterDTO) => setting.name === featureName);

/**
 * Processes HTML content to automatically convert plain text URLs into clickable links
 * and ensures all links open in new tabs with proper security attributes.
 * 
 * @param text - The input text that may contain URLs or HTML content
 * @returns Processed HTML string with clickable links
 * 
 * Features:
 * - Automatically detects and converts plain text URLs (http/https) to clickable links
 * - Escapes HTML characters to prevent XSS attacks
 * - Adds target="_blank" and rel="noopener noreferrer" to all links
 * - Supports both plain text URLs and existing HTML links
 * 
 * Example:
 * Input: "Visit https://example.com for more info"
 * Output: "Visit <a href='https://example.com' target='_blank' rel='noopener noreferrer'>https://example.com</a> for more info"
 */
export const processHtmlContent = (text: string): string => {
  // First, escape any existing HTML to prevent XSS
  const escapedText = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // URL regex pattern to match http/https URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Replace URLs with clickable links
  const textWithLinks = escapedText.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);

  // Also handle existing HTML links if any
  return textWithLinks.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
    if (attributes.includes('target=')) {
      return match;
    }
    return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
  });
};

/**
 * Helper function to determine if premium feature is enabled
 * @param page - The menu item page object
 * @param configParameter - The config parameter data
 * @returns boolean indicating if the premium feature is enabled
 */
export const isPageEnabled = (
  page: {
    isEnabled?: boolean;
    ABSOLUTE_PATH: string;
    configParameter?: string;
  },
  configParameter: any
): boolean => {
  // If page has its own isEnabled, use it directly
  if (page.isEnabled !== undefined) {
    return page.isEnabled;
  }

  // Apply config parameter to specific pages
  const configControlledPages = [SCORECARD_PAGE.ABSOLUTE_PATH, TARGETS_PAGE.ABSOLUTE_PATH, INITIATIVES_PAGE.ABSOLUTE_PATH]; // Add more paths as needed
  if (configControlledPages.includes(page.ABSOLUTE_PATH) && page.configParameter) {
    return retrieveConfigParameter(configParameter, page.configParameter)?.configValue === "true";
  }

  return true; // Default to enabled
};

/**
 * Returns the color state props for a text field when an error exists for the given key.
 *
 * @template T - The type of the utility bill form data.
 * @param {keyof UtilityBillFormData} key - The name of the field to check for validation errors.
 * @param {FieldErrors<UtilityBillFormData>} formStateErrors - The collection of field errors from the form state.
 * @returns {TextFieldColorState} The color state props indicating an error, including the error message.
 *
 * @example
 * const errorProps = getErrorColorStateProps("amount", formState.errors);
 * // Returns: { color: "error", message: "This field is required" }
 */
export const getErrorColorStateProps: (
  key: keyof UtilityBillFormData,
  formStateErrors: FieldErrors<UtilityBillFormData>,
) => TextFieldColorState = (key, formStateErrors) => ({
  color: "error",
  message:
    formStateErrors[key]?.type === "required"
      ? REQUIRED_VALIDATION_MESSAGE
      : formStateErrors[key]?.message,
});

/**
 * Returns the color state props for a text field when the specified field is marked as a warning.
 *
 * - If the field has a warning, assigns `"warning"` as the color and retrieves the corresponding message.
 * - If the field is not marked as a warning, both `color` and `message` are `undefined`.
 *
 * @template T - The type of the utility bill form data.
 * @param {keyof UtilityBillFormData} key - The field key to check for warning status.
 * @param {Record<keyof UtilityBillFormData, boolean>} isWarningFields - A record indicating which fields have warning states.
 * @returns {TextFieldColorState} The color state props containing `"warning"` and a contextual message if applicable.
 *
 * @example
 * const warningProps = getWarningColorStateProps("usage", warningFields);
 * // Returns: { color: "warning", message: "Usage unusually high" }
 *
 * @example
 * const noWarning = getWarningColorStateProps("amount", warningFields);
 * // Returns: { color: undefined, message: undefined }
 */
export const getWarningColorStateProps = (
  key: keyof UtilityBillFormData,
  isWarningFields: Record<keyof UtilityBillFormData, boolean>,
): TextFieldColorState => ({
  color: isWarningFields[key] ? "warning" : undefined,
  message: isWarningFields[key]
    ? (WARNING_MESSAGES[key as keyof typeof WARNING_MESSAGES] as string)
    : undefined,
});

/**
 * Determines the color state props for a text field based on form validation errors
 * or warning field states.
 *
 * - If the field has a validation error, returns error color state props.
 * - If there’s no error but a warning is present, returns warning color state props.
 * - If neither applies, returns an empty object.
 *
 * @template T - The type of the utility bill form data.
 * @param {keyof UtilityBillFormData} key - The field key to evaluate.
 * @param {FieldErrors<UtilityBillFormData>} formStateErrors - The collection of field errors from the form state.
 * @param {Record<keyof UtilityBillFormData, boolean>} [isWarningFields] - Optional record indicating which fields have warnings.
 * @returns {TextFieldColorState | {}} The appropriate color state props, or an empty object if no warnings or errors exist.
 *
 * @example
 * const props = getTextFieldColorStateProps("amount", formState.errors, warningFields);
 * // Returns: { color: "error", message: "Invalid amount" } or { color: "warning", message: "Unusually high value" }
 */
export const getTextFieldColorStateProps = (
  key: keyof UtilityBillFormData,
  formStateErrors: FieldErrors<UtilityBillFormData>,
  isWarningFields?: Record<keyof UtilityBillFormData, boolean>,
) =>
  formStateErrors[key]
    ? getErrorColorStateProps(key, formStateErrors)
    : isWarningFields
      ? getWarningColorStateProps(key, isWarningFields)
      : {};

/**
 * Converts an object of applied filters into an array of column filter objects,
 * optionally remapping filter keys using a provided column ID map.
 *
 * This utility is typically used to transform local filter state into a format
 * consumable by table components or APIs that expect `{ id, value }` pairs.
 *
 * @param {Record<string, any>} appliedFilters - Object containing key-value pairs
 * representing currently applied filters. Keys correspond to field names,
 * and values represent filter values.
 *
 * @param {Object<string, string>} [columnIdMap] - Optional mapping of filter keys
 * to alternative column IDs. If provided, keys found in this map will be replaced
 * by their corresponding values. Keys not found will fall back to their original names.
 *
 * @returns {{ id: string, value: string }[]} An array of filter objects containing
 * only non-null and non-empty filters, with `id` (possibly remapped) and `value` as strings.
 *
 */
export const getColumnFiltersFromApplied = (
  appliedFilters: any,
  columnIdMap?: { [key: string]: string }
) => Object.entries(appliedFilters)
  .filter(([, value]) => value !== null && value !== '')
  .map(([key, value]) => {
    if (!columnIdMap) {
      return {
        id: key,
        value: value as string
      };
    }

    return {
      id: columnIdMap[key] || key,
      value: value as string
    };
  });

/**
 * Safely parses a JSON string and returns a fallback value if parsing fails.
 *
 * @template T
 * @param {string | null} raw - The raw JSON string to parse (e.g. from localStorage).
 * @param {T} defaultValue - The value to return if the input is null or parsing fails.
 * @returns {T} The parsed JSON object, or the provided default value on failure.
 *
 * @example
 * const user = safeParseJSON<User>(localStorage.getItem("user"), null);
 */
export const safeParseJSON = <T,>(raw: string | null, defaultValue: T): T => {
  if (!raw) return defaultValue;
  try {
    return JSON.parse(raw);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to parse localStorage value:", error);
    return defaultValue;
  }
};

export const getEmissionType: (arg0: boolean, arg1: boolean, arg2: string | undefined, arg3?: boolean) => string = (
  isEmissionsChart,
  isConsumption,
  subTabName,
  isEstimate,
) => isEmissionsChart ? `${subTabName}Based${isEstimate ? "Estimate" : "Emission"}` : isConsumption ? "consumption" : "amount";

export const generateXAxisCategories: (arg0: any[], arg1: string) => string[] = (
  data,
  dateRangeType,
) => Array.from(new Set(data
  .filter((entry: any) => {
    // Filter out entries with missing required fields
    if (dateRangeType === "MONTHLY") {
      return entry.year !== null && entry.year !== undefined && entry.month;
    }
    if (dateRangeType === "QUARTERLY") {
      return entry.fiscalYear !== null && entry.fiscalYear !== undefined &&
        entry.fiscalYearQuarter !== null && entry.fiscalYearQuarter !== undefined;
    }
    return entry.fiscalYear !== null && entry.fiscalYear !== undefined;
  })
  .map((entry: any) =>
    dateRangeType === "MONTHLY"
      ? entry.month + entry.year.replace("20", " '")
      : dateRangeType === "QUARTERLY"
        ? (`Q${entry.fiscalYearQuarter}`).concat(entry.fiscalYear.replace("20", " '")).concat(" FY")
        : entry.fiscalYear.concat(" FY")
  )
));

export const generateXAxisData: (arg0: Record<string, any[]>, arg1: string[], arg2: string, arg3: string) => Record<string, any[]> = (
  dataTypeArr,
  sortedXAxisCategoriesGenerated,
  dateRangeType,
  emissionType,
) => Object.keys(dataTypeArr).reduce((acc: any, arrKey: string) => {
  acc[arrKey] = sortedXAxisCategoriesGenerated.map((entry: string) => {
    const value = dataTypeArr[arrKey as keyof typeof dataTypeArr].filter((item: any) => {
      if (dateRangeType === "MONTHLY") {
        return `${item.month} '${item.year?.substring(2, 4)}` === entry;
      }
      if (dateRangeType === "QUARTERLY") {
        return (
          item.fiscalYearQuarter === entry?.substring(1, 2) &&
          item.fiscalYear?.substring(2) === entry?.substring(4, 6)
        );
      }
      return item.fiscalYear === entry?.substring(0, 4);
    }).reduce((sum: number, curr: any) => sum + (curr?.[emissionType] ?? 0), 0);
    return value;
  });
  return acc;
}, {});